import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { shouldShowPerformanceMonitor } from "../utils/environment";

/**
 * Performance Monitor Component
 *
 * This component provides real-time performance metrics and debugging tools.
 *
 * VISIBILITY:
 * - 🟢 DEVELOPMENT: Always visible (localhost, dev domains, NODE_ENV=development)
 * - 🔴 PRODUCTION: Hidden by default for end users
 * - 🟡 PRODUCTION DEBUG: Add ?debug=plum2024 to URL to enable for debugging
 *
 * USAGE:
 * - Click "Show Perf" button to display metrics
 * - Press Ctrl+P to quickly toggle visibility
 * - Toggle Performance Mode to reduce resource usage
 *
 * METRICS:
 * - FPS: Frames per second (Green: 55+, Yellow: 30-55, Red: <30)
 * - Frame Time: Average time per frame in milliseconds
 * - Memory: JavaScript heap usage (if available)
 * - DOM Nodes: Total DOM elements count
 * - Canvas: Number of canvas elements
 * - Performance Mode: Shows if optimization mode is active
 */

const MonitorContainer = styled.div`
  position: fixed;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 10px;
  border-radius: 5px;
  font-family: "Courier New", monospace;
  font-size: 12px;
  z-index: 9999;
  min-width: 200px;
  user-select: none;
  pointer-events: none;
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 3px;
`;

const MetricLabel = styled.span`
  color: #ccc;
`;

const MetricValue = styled.span`
  color: ${props => {
    if (props.type === "fps") {
      return props.value >= 55 ? "#0f0" : props.value >= 30 ? "#ff0" : "#f00";
    }
    if (props.type === "memory") {
      return props.value < 50 ? "#0f0" : props.value < 100 ? "#ff0" : "#f00";
    }
    return "#fff";
  }};
  font-weight: bold;
`;

const ToggleButton = styled.button`
  position: fixed;
  top: 10px;
  right: 220px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: 1px solid #333;
  border-radius: 3px;
  padding: 5px 10px;
  font-size: 10px;
  cursor: pointer;
  z-index: 9999;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const PerformanceMonitor = ({
  enabled = true,
  onPerformanceToggle,
  performanceMode = false,
  fps = 0,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [metrics, setMetrics] = useState({
    fps: 0,
    frameTime: 0,
    memory: 0,
    renderCount: 0,
    domNodes: 0,
    canvasCount: 0,
    threejsObjects: 0,
  });

  const frameCountRef = useRef(0);
  const lastTimeRef = useRef(performance.now());
  const lastFpsUpdateRef = useRef(performance.now());
  const animationFrameRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const updateMetrics = () => {
      const now = performance.now();
      frameCountRef.current++;

      // Calculate FPS every second
      if (now - lastFpsUpdateRef.current >= 1000) {
        const fps = Math.round(
          (frameCountRef.current * 1000) / (now - lastFpsUpdateRef.current)
        );
        const frameTime =
          (now - lastFpsUpdateRef.current) / frameCountRef.current;

        // Get memory usage (if available)
        let memoryUsage = 0;
        if (performance.memory) {
          memoryUsage = Math.round(
            performance.memory.usedJSHeapSize / 1024 / 1024
          );
        }

        // Count DOM elements
        const domNodes = document.querySelectorAll("*").length;

        // Count canvas elements
        const canvasCount = document.querySelectorAll("canvas").length;

        // Count Three.js related objects (rough estimate)
        const threejsObjects = document.querySelectorAll(
          '[data-engine="three.js"]'
        ).length;

        setMetrics({
          fps,
          frameTime: Math.round(frameTime * 100) / 100,
          memory: memoryUsage,
          renderCount: frameCountRef.current,
          domNodes,
          canvasCount,
          threejsObjects,
        });

        frameCountRef.current = 0;
        lastFpsUpdateRef.current = now;
      }

      lastTimeRef.current = now;
      animationFrameRef.current = requestAnimationFrame(updateMetrics);
    };

    if (isVisible) {
      animationFrameRef.current = requestAnimationFrame(updateMetrics);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [enabled, isVisible]);

  // Keyboard shortcut to toggle
  useEffect(() => {
    const handleKeyPress = event => {
      if (event.key === "p" && event.ctrlKey) {
        event.preventDefault();
        setIsVisible(prev => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  // Only show in development environment or when debug mode is enabled
  if (!shouldShowPerformanceMonitor()) {
    return null;
  }

  if (!enabled) return null;

  return (
    <>
      <ToggleButton onClick={() => setIsVisible(!isVisible)}>
        {isVisible ? "Hide" : "Show"} Perf
      </ToggleButton>

      {isVisible && (
        <MonitorContainer>
          <MetricRow>
            <MetricLabel>FPS:</MetricLabel>
            <MetricValue type="fps" value={fps || metrics.fps}>
              {fps || metrics.fps}
            </MetricValue>
          </MetricRow>

          <MetricRow>
            <MetricLabel>Frame Time:</MetricLabel>
            <MetricValue>{metrics.frameTime}ms</MetricValue>
          </MetricRow>

          {performance.memory && (
            <MetricRow>
              <MetricLabel>Memory:</MetricLabel>
              <MetricValue type="memory" value={metrics.memory}>
                {metrics.memory}MB
              </MetricValue>
            </MetricRow>
          )}

          <MetricRow>
            <MetricLabel>DOM Nodes:</MetricLabel>
            <MetricValue>{metrics.domNodes}</MetricValue>
          </MetricRow>

          <MetricRow>
            <MetricLabel>Canvas:</MetricLabel>
            <MetricValue>{metrics.canvasCount}</MetricValue>
          </MetricRow>

          <MetricRow>
            <MetricLabel>Renders:</MetricLabel>
            <MetricValue>{metrics.renderCount}</MetricValue>
          </MetricRow>

          <MetricRow>
            <MetricLabel>Perf Mode:</MetricLabel>
            <MetricValue style={{ color: performanceMode ? "#ff0" : "#0f0" }}>
              {performanceMode ? "ON" : "OFF"}
            </MetricValue>
          </MetricRow>

          {onPerformanceToggle && (
            <div style={{ marginTop: "10px" }}>
              <button
                onClick={() => onPerformanceToggle(!performanceMode)}
                style={{
                  background: performanceMode ? "#ff4444" : "#44ff44",
                  color: "white",
                  border: "none",
                  padding: "4px 8px",
                  borderRadius: "3px",
                  fontSize: "10px",
                  cursor: "pointer",
                }}
              >
                {performanceMode ? "Disable" : "Enable"} Performance Mode
              </button>
            </div>
          )}

          <div style={{ marginTop: "10px", fontSize: "10px", color: "#999" }}>
            Ctrl+P to toggle
          </div>
        </MonitorContainer>
      )}
    </>
  );
};

export default PerformanceMonitor;
